import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Switch } from "../components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { FileStack, Copy, Trash2, Eye, Plus, Save, Wand2, Sparkles, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Friendly labels for the 10 supported kinds (matches backend TEMPLATE_KINDS)
const KIND_LABELS = {
  quote: "Quotes",
  invoice: "Invoices",
  job_sheet: "Job Sheets",
  safety_sheet: "Safety Sheets",
  training_sheet: "Training Sheets",
  payroll: "Payroll",
  email_reply: "Email Replies",
  text_reply: "Text Replies",
  portal_msg_reply: "Portal Replies",
  custom: "Custom",
};

const KINDS = Object.keys(KIND_LABELS);

export default function UniversalTemplates() {
  const [activeKind, setActiveKind] = useState("quote");
  const [templates, setTemplates] = useState([]);
  const [selected, setSelected] = useState(null);
  const [placeholders, setPlaceholders] = useState({});
  const [preview, setPreview] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [newName, setNewName] = useState("");
  const [newDesc, setNewDesc] = useState("");
  const [aiBusy, setAiBusy] = useState(null); // index of field currently being AI-filled
  const [aiContext, setAiContext] = useState("");
  const [aiTone, setAiTone] = useState("professional");

  const loadAll = async () => {
    try {
      const [t, p] = await Promise.all([
        axios.get(`${API}/universal-templates/`, { params: { kind: activeKind } }),
        axios.get(`${API}/universal-templates/placeholders`),
      ]);
      setTemplates(t.data.templates || []);
      setPlaceholders(p.data || {});
    } catch (e) {
      toast.error("Failed to load templates");
    }
  };

  useEffect(() => { loadAll(); /* eslint-disable-next-line */ }, [activeKind]);

  const filtered = useMemo(() => templates.filter((t) => t.kind === activeKind), [templates, activeKind]);

  const createTemplate = async () => {
    if (!newName.trim()) return toast.error("Name required");
    try {
      const res = await axios.post(`${API}/universal-templates/`, {
        kind: activeKind, name: newName, description: newDesc,
      });
      setNewName(""); setNewDesc(""); setCreateOpen(false);
      toast.success("Template created");
      await loadAll();
      setSelected(res.data);
    } catch (e) { toast.error("Failed to create"); }
  };

  const save = async () => {
    if (!selected) return;
    try {
      const res = await axios.put(`${API}/universal-templates/${selected.id}`, {
        kind: selected.kind,
        name: selected.name,
        description: selected.description,
        field_schema: selected.field_schema,
        layout_html: selected.layout_html,
        is_active: selected.is_active,
      });
      setSelected(res.data);
      toast.success("Saved");
      loadAll();
    } catch (e) { toast.error("Failed to save"); }
  };

  const duplicate = async (id) => {
    try {
      const res = await axios.post(`${API}/universal-templates/${id}/duplicate`);
      toast.success("Duplicated");
      await loadAll();
      setSelected(res.data);
    } catch (e) { toast.error("Failed to duplicate"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Archive this template?")) return;
    try {
      await axios.delete(`${API}/universal-templates/${id}`);
      toast.success("Archived");
      if (selected?.id === id) setSelected(null);
      loadAll();
    } catch (e) { toast.error("Failed to archive"); }
  };

  const renderPreview = async () => {
    if (!selected) return;
    try {
      // save first so preview reflects unsaved edits
      await save();
      const res = await axios.post(`${API}/universal-templates/${selected.id}/render`, {});
      setPreview(res.data);
    } catch (e) { toast.error("Render failed"); }
  };

  const toggleField = (idx) => {
    setSelected((s) => {
      const fs = [...(s.field_schema || [])];
      fs[idx] = { ...fs[idx], enabled: !fs[idx].enabled };
      return { ...s, field_schema: fs };
    });
  };

  const updateFieldValue = (idx, value) => {
    setSelected((s) => {
      const fs = [...(s.field_schema || [])];
      fs[idx] = { ...fs[idx], default_value: value };
      return { ...s, field_schema: fs };
    });
  };

  const insertPlaceholder = (ph) => {
    if (!selected) return;
    setSelected((s) => ({ ...s, layout_html: (s.layout_html || "") + ph }));
  };

  // Phase E — AI writing assistance for an individual field
  const aiFillField = async (idx) => {
    if (!selected) return;
    const field = selected.field_schema[idx];
    setAiBusy(idx);
    try {
      const res = await axios.post(`${API}/ai-writer/generate`, {
        kind: selected.kind,
        field_label: field.label,
        business_context: aiContext || selected.description || "",
        tone: aiTone,
        max_words: 120,
      });
      updateFieldValue(idx, res.data.text);
      toast.success("AI wording inserted");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "AI generation failed");
    } finally {
      setAiBusy(null);
    }
  };

  return (
    <div className="p-6 space-y-6" data-testid="universal-templates-page">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2"><FileStack className="h-7 w-7 text-emerald-600" /> Universal Templates</h1>
          <p className="text-muted-foreground mt-1">Build reusable templates for quotes, invoices, safety/training sheets, and reply messages. Toggle fields on/off, set defaults, and insert <code className="px-1 bg-muted rounded">{`{{placeholders}}`}</code> like <code className="px-1 bg-muted rounded">{`{{company.legal_name}}`}</code>.</p>
        </div>
        <Dialog open={createOpen} onOpenChange={setCreateOpen}>
          <DialogTrigger asChild>
            <Button className="bg-emerald-600 hover:bg-emerald-700" data-testid="new-template-btn">
              <Plus className="h-4 w-4 mr-1" /> New {KIND_LABELS[activeKind]} Template
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>New {KIND_LABELS[activeKind]} Template</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <Input placeholder="Template name" value={newName} onChange={(e) => setNewName(e.target.value)} data-testid="new-template-name" />
              <Textarea placeholder="Description (optional)" value={newDesc} onChange={(e) => setNewDesc(e.target.value)} data-testid="new-template-desc" />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button>
              <Button onClick={createTemplate} className="bg-emerald-600 hover:bg-emerald-700" data-testid="confirm-create-template-btn">Create</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* Kind tabs */}
      <div className="flex flex-wrap gap-1 border-b pb-2" data-testid="kind-tabs">
        {KINDS.map((k) => (
          <button
            key={k}
            onClick={() => { setActiveKind(k); setSelected(null); setPreview(null); }}
            data-testid={`kind-tab-${k}`}
            className={`px-3 py-1.5 text-sm rounded-md transition ${activeKind === k ? "bg-emerald-100 text-emerald-700 font-medium" : "text-muted-foreground hover:bg-muted"}`}
          >
            {KIND_LABELS[k]}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Template list */}
        <Card className="lg:col-span-1" data-testid="template-list-card">
          <CardHeader><CardTitle className="text-base">{KIND_LABELS[activeKind]} Templates ({filtered.length})</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            {filtered.length === 0 ? (
              <p className="text-sm text-muted-foreground italic" data-testid="template-list-empty">No templates yet. Click "New Template" to create one.</p>
            ) : filtered.map((t) => (
              <div key={t.id} className={`p-2 border rounded-md cursor-pointer transition hover:border-emerald-400 ${selected?.id === t.id ? "border-emerald-500 bg-emerald-50/50" : ""}`} onClick={() => { setSelected(t); setPreview(null); }} data-testid={`template-card-${t.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="text-sm font-medium truncate">{t.name}</p>
                    <p className="text-xs text-muted-foreground truncate">{t.description || "—"}</p>
                  </div>
                  <div className="flex gap-1">
                    {t.is_default && <Badge variant="outline" className="text-xs">default</Badge>}
                  </div>
                </div>
                <div className="flex gap-1 mt-2">
                  <Button size="sm" variant="ghost" className="h-7 px-2" onClick={(e) => { e.stopPropagation(); duplicate(t.id); }} data-testid={`duplicate-template-${t.id}`}><Copy className="h-3 w-3" /></Button>
                  {!t.is_default && (<Button size="sm" variant="ghost" className="h-7 px-2 text-rose-600" onClick={(e) => { e.stopPropagation(); remove(t.id); }} data-testid={`delete-template-${t.id}`}><Trash2 className="h-3 w-3" /></Button>)}
                </div>
              </div>
            ))}
          </CardContent>
        </Card>

        {/* Editor */}
        <Card className="lg:col-span-2" data-testid="template-editor">
          {!selected ? (
            <CardContent className="p-8 text-center text-muted-foreground">Select a template on the left to edit it, or create a new one.</CardContent>
          ) : (
            <>
              <CardHeader>
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <Input value={selected.name} onChange={(e) => setSelected({ ...selected, name: e.target.value })} className="text-base font-semibold max-w-md" data-testid="template-name-input" />
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={renderPreview} data-testid="preview-template-btn"><Eye className="h-4 w-4 mr-1" /> Preview</Button>
                    <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={save} data-testid="save-template-btn"><Save className="h-4 w-4 mr-1" /> Save</Button>
                  </div>
                </div>
                <Textarea value={selected.description || ""} onChange={(e) => setSelected({ ...selected, description: e.target.value })} rows={2} placeholder="Description / when to use this template" className="mt-2" data-testid="template-desc-input" />
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Field schema */}
                <div>
                  <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                    <h3 className="text-sm font-semibold">Toggleable Fields</h3>
                    <div className="flex items-center gap-2 flex-wrap">
                      <label className="text-xs text-muted-foreground">AI tone:</label>
                      <select value={aiTone} onChange={(e) => setAiTone(e.target.value)} className="border rounded-md px-2 py-1 text-xs h-8" data-testid="ai-tone-select">
                        <option value="professional">Professional</option>
                        <option value="warm">Warm</option>
                        <option value="concise">Concise</option>
                        <option value="formal">Formal</option>
                        <option value="friendly">Friendly</option>
                      </select>
                      <label className="text-xs text-muted-foreground">Context:</label>
                      <Input value={aiContext} onChange={(e) => setAiContext(e.target.value)} placeholder="e.g. residential window cleaning" className="h-8 text-xs w-56" data-testid="ai-context-input" />
                    </div>
                  </div>
                  <div className="space-y-2" data-testid="field-schema-list">
                    {(selected.field_schema || []).map((f, idx) => (
                      <div key={f.id || idx} className="p-3 border rounded-md space-y-2">
                        <div className="flex items-center justify-between gap-2">
                          <div>
                            <p className="text-sm font-medium">{f.label}</p>
                            <p className="text-xs text-muted-foreground">{f.id} • {f.type}</p>
                          </div>
                          <div className="flex items-center gap-2">
                            {f.enabled !== false && (
                              <Button size="sm" variant="outline" className="h-7 px-2 text-xs" onClick={() => aiFillField(idx)} disabled={aiBusy === idx} data-testid={`ai-fill-${f.id || idx}`}>
                                {aiBusy === idx ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Sparkles className="h-3 w-3 mr-1 text-emerald-600" />}
                                AI Fill
                              </Button>
                            )}
                            <Switch checked={f.enabled !== false} onCheckedChange={() => toggleField(idx)} data-testid={`field-toggle-${f.id || idx}`} />
                          </div>
                        </div>
                        {f.enabled !== false && (
                          <Textarea value={f.default_value || ""} onChange={(e) => updateFieldValue(idx, e.target.value)} rows={f.type === "rich_text" ? 3 : 1} placeholder="Default text (supports {{company.legal_name}} etc.)" className="text-sm" data-testid={`field-value-${f.id || idx}`} />
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Free-form layout */}
                <div>
                  <h3 className="text-sm font-semibold mb-2">Optional Layout / Footer (HTML)</h3>
                  <Textarea value={selected.layout_html || ""} onChange={(e) => setSelected({ ...selected, layout_html: e.target.value })} rows={6} placeholder="Optional HTML/Markdown layout — leave blank if you only want toggleable fields." className="font-mono text-xs" data-testid="layout-html-input" />
                  <div className="mt-2 flex flex-wrap gap-1">
                    <p className="text-xs text-muted-foreground w-full">Click a placeholder to insert it into the layout:</p>
                    {Object.entries(placeholders).map(([group, items]) =>
                      items.map((ph) => (
                        <button key={ph} type="button" onClick={() => insertPlaceholder(ph)} className="text-xs px-2 py-0.5 border rounded-full bg-muted/30 hover:bg-muted" data-testid={`placeholder-${ph.replace(/[{}.\s]/g, "")}`}>{ph}</button>
                      ))
                    )}
                  </div>
                </div>

                {/* Preview */}
                {preview && (
                  <div className="p-3 bg-muted/30 rounded-md border" data-testid="preview-panel">
                    <h3 className="text-sm font-semibold mb-2 flex items-center gap-1"><Wand2 className="h-4 w-4 text-emerald-600" /> Live Preview ({preview.name})</h3>
                    <div className="space-y-2">
                      {preview.rendered_fields.map((f) => (
                        <div key={f.id}>
                          <p className="text-xs font-medium text-muted-foreground">{f.label}</p>
                          <p className="text-sm whitespace-pre-wrap">{f.value || <em className="text-muted-foreground">empty</em>}</p>
                        </div>
                      ))}
                      {preview.rendered_layout && (
                        <div>
                          <p className="text-xs font-medium text-muted-foreground mt-2">Layout</p>
                          <pre className="text-xs whitespace-pre-wrap bg-white p-2 rounded border">{preview.rendered_layout}</pre>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </CardContent>
            </>
          )}
        </Card>
      </div>
    </div>
  );
}
